import type { StripePaymentMethod } from "@api/private/getStripePaymentMethods";
import CreditCardManagementPanel from "@components/creditCard/CreditCardManagementPanel";
import { externalLinks } from "@constants/links";
import { Box, Button, Checkbox, FormControlLabel, Typography } from "@mui/material";
import type { StripeCardElementChangeEvent } from "@stripe/stripe-js";
import { formatCurrency, type SupportedCurrency } from "@utils/currencyUtils";
import { type Control, Controller, type ControllerRenderProps } from "react-hook-form";
import CreditCardDisplay from "../creditCard/CreditCardDisplay";

interface ConfirmAuthenticatedProps {
  selectedPaymentMethod: string | null;
  displayCard?: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
  isPaymentDialogOpen: boolean;
  onOpenDialog: () => void;
  onCloseDialog: () => void;
  handleSavedMethodChange: (paymentMethodId: string | null) => void;
  defaultPaymentMethodId: string | null;
  savedPaymentMethods: StripePaymentMethod[];
  isLoadingPaymentMethods: boolean;
  paymentMethodsError?: Error | null | undefined;
  cardError: string | null;
  handleCreditCardChange: (event: StripeCardElementChangeEvent) => void;
  refetchPaymentMethods: () => void | Promise<void>;
  totalWithTax?: number;
  selectedCurrency: SupportedCurrency;
  control: Control<Record<string, unknown>>;
  handleAgreementChange: (
    isChecked: boolean,
    field: ControllerRenderProps<Record<string, unknown>, "agreement">,
  ) => void;
  loading: boolean;
  isAuthUpdatingPurchase: boolean;
}

export default function ConfirmAuthenticated({
  selectedPaymentMethod,
  control,
  handleAgreementChange,
  loading,
  isAuthUpdatingPurchase,
  displayCard,
  isPaymentDialogOpen,
  onOpenDialog,
  onCloseDialog,
  handleSavedMethodChange,
  defaultPaymentMethodId,
  savedPaymentMethods,
  isLoadingPaymentMethods,
  paymentMethodsError,
  cardError,
  handleCreditCardChange,
  refetchPaymentMethods,
  totalWithTax,
  selectedCurrency,
}: ConfirmAuthenticatedProps) {
  const hasDisplayCard =
    !!displayCard && selectedPaymentMethod !== null && selectedPaymentMethod !== "new_card";
  const actionButtonLabel = hasDisplayCard
    ? "Change Default Payment Method"
    : "Add a Payment Method";
  const isAddCta = !hasDisplayCard;

  return (
    <Box>
      {hasDisplayCard && <CreditCardDisplay card={displayCard} />}
      <Button
        className="mb-5"
        variant={isAddCta ? "contained" : "text"}
        color="primary"
        sx={{
          mt: 2,
        }}
        onClick={onOpenDialog}
      >
        {actionButtonLabel}
      </Button>
      <CreditCardManagementPanel
        open={isPaymentDialogOpen}
        onClose={onCloseDialog}
        selectedPaymentMethod={selectedPaymentMethod}
        handleSavedMethodChange={handleSavedMethodChange}
        defaultPaymentMethodId={defaultPaymentMethodId}
        savedPaymentMethods={savedPaymentMethods}
        isLoadingPaymentMethods={isLoadingPaymentMethods}
        paymentMethodsError={paymentMethodsError ?? null}
        cardError={cardError}
        handleCreditCardChange={handleCreditCardChange}
        refetchPaymentMethods={refetchPaymentMethods}
      />

      {totalWithTax !== null && totalWithTax !== undefined && (
        <Typography variant="caption" mt={4} display="block" fontWeight={600}>
          You will be charged {formatCurrency(totalWithTax, selectedCurrency)} today.
        </Typography>
      )}

      <Box mt={4}>
        <AgreementControl
          control={control}
          handleAgreementChange={handleAgreementChange}
          loading={loading}
          isAuthUpdatingPurchase={isAuthUpdatingPurchase}
        />
      </Box>
    </Box>
  );
}

interface AgreementControlProps {
  control: Control<Record<string, unknown>>;
  handleAgreementChange: (
    isChecked: boolean,
    field: ControllerRenderProps<Record<string, unknown>, "agreement">,
  ) => void;
  loading: boolean;
  isAuthUpdatingPurchase: boolean;
}

function AgreementControl({
  control,
  handleAgreementChange,
  loading,
  isAuthUpdatingPurchase,
}: AgreementControlProps) {
  return (
    <Controller
      name="agreement"
      control={control}
      rules={{ required: true }}
      render={({ field }) => (
        <FormControlLabel
          control={
            <Checkbox
              {...field}
              size="small"
              checked={Boolean(field.value)}
              disabled={loading || isAuthUpdatingPurchase}
              onChange={(e) => handleAgreementChange(e.target.checked, field)}
            />
          }
          label={
            <Typography variant="caption">
              I accept the Unyte Health{" "}
              <a href={externalLinks.termsOfService} target="_blank" rel="noopener noreferrer">
                Customer Terms of Service
              </a>{" "}
              including the Professional Attestation.
            </Typography>
          }
        />
      )}
    />
  );
}
